import React, { useState, useEffect, useRef } from "react";
import { Typography, styled, keyframes, Box } from "@mui/material";

// ================== KEYFRAME ANIMATIONS ==================

const typewriterCursor = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const bounceIn = keyframes`
  0% {
    transform: scale(0.3) translateY(-100px);
    opacity: 0;
  }
  50% {
    transform: scale(1.05) translateY(0);
  }
  70% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const elastic = keyframes`
  0% {
    transform: scale(0) rotate(-360deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(-180deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
`;

const wave = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const slideInUpStagger = keyframes`
  0% {
    transform: translateY(50px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeInChar = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    text-shadow: 0 0 5px rgba(41, 188, 98, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(41, 188, 98, 0.8), 0 0 30px rgba(41, 188, 98, 0.4);
  }
`;

const flipIn = keyframes`
  0% {
    transform: rotateY(-90deg);
    opacity: 0;
  }
  100% {
    transform: rotateY(0);
    opacity: 1;
  }
`;

// ================== ANIMATION COMPONENTS ==================

interface BaseTextProps {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

// 1. Typewriter Effect Component
interface TypewriterTextProps extends Omit<BaseTextProps, "children"> {
  text: string;
  speed?: number;
  showCursor?: boolean;
  cursorChar?: string;
}

const TypewriterContainer = styled(Box)({
  display: "inline-block",
});

const TypewriterCursor = styled("span")({
  animation: `${typewriterCursor} 1s infinite`,
  marginLeft: "2px",
});

export const TypewriterText: React.FC<TypewriterTextProps> = ({
  text,
  speed = 100,
  delay = 0,
  showCursor = true,
  cursorChar = "|",
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  const [displayText, setDisplayText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!trigger) {
      setDisplayText("");
      setIsComplete(false);
      return;
    }

    const startAnimation = () => {
      let currentIndex = 0;

      const typeNextChar = () => {
        if (currentIndex <= text.length) {
          setDisplayText(text.slice(0, currentIndex));
          currentIndex++;

          if (currentIndex <= text.length) {
            timeoutRef.current = setTimeout(typeNextChar, speed);
          } else {
            setIsComplete(true);
          }
        }
      };

      timeoutRef.current = setTimeout(typeNextChar, delay);
    };

    startAnimation();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [text, speed, delay, trigger]);

  return (
    <Component sx={sx}>
      <TypewriterContainer>
        {displayText}
        {showCursor && (!isComplete || displayText.length === 0) && <TypewriterCursor>{cursorChar}</TypewriterCursor>}
      </TypewriterContainer>
    </Component>
  );
};

// 2. Staggered Reveal Animation (Letters/Words)
interface StaggeredTextProps {
  text: string;
  staggerBy?: "letters" | "words";
  staggerDelay?: number;
  delay?: number;
  duration?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

const StaggeredChar = styled("span", {
  shouldForwardProp: (prop) => !['delay', 'trigger'].includes(prop as string),
})<{ delay: number; trigger: boolean }>(({ delay, trigger }) => ({
  display: "inline-block",
  opacity: trigger ? 1 : 0,
  transform: trigger ? "translateY(0) scale(1)" : "translateY(20px) scale(0.8)",
  animation: trigger ? `${fadeInChar} 0.6s ease-out ${delay}s both` : "none",
  transition: "all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
}));

const StaggeredWord = styled("span", {
  shouldForwardProp: (prop) => !['delay', 'trigger'].includes(prop as string),
})<{ delay: number; trigger: boolean }>(({ delay, trigger }) => ({
  display: "inline-block",
  marginRight: "0.3em",
  opacity: trigger ? 1 : 0,
  transform: trigger ? "translateY(0)" : "translateY(50px)",
  animation: trigger ? `${slideInUpStagger} 0.8s ease-out ${delay}s both` : "none",
}));

export const StaggeredText: React.FC<StaggeredTextProps> = ({
  text,
  staggerBy = "letters",
  staggerDelay = 0.05,
  delay = 0,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  if (staggerBy === "words") {
    const words = text.split(" ");
    return (
      <Component sx={sx}>
        {words.map((word, index) => (
          <StaggeredWord key={index} delay={delay + index * staggerDelay} trigger={trigger}>
            {word}
          </StaggeredWord>
        ))}
      </Component>
    );
  }

  return (
    <Component sx={sx}>
      {text.split("").map((char, index) => (
        <StaggeredChar key={index} delay={delay + index * staggerDelay} trigger={trigger}>
          {char === " " ? "\u00A0" : char}
        </StaggeredChar>
      ))}
    </Component>
  );
};

// 3. Bounce Effect Component
const BounceText = styled(Typography, {
  shouldForwardProp: (prop) => !['delay', 'trigger'].includes(prop as string),
})<{ delay: number; trigger: boolean }>(({ delay, trigger }) => ({
  opacity: trigger ? 1 : 0,
  animation: trigger ? `${bounceIn} 1s ease-out ${delay}s both` : "none",
  transformOrigin: "center bottom",
}));

export const BouncingText: React.FC<BaseTextProps> = ({
  children,
  delay = 0,
  trigger = true,
  component = Typography,
  sx = {},
}) => {
  const Component = component;
  return (
    <BounceText as={Component} delay={delay} trigger={trigger} sx={sx}>
      {children}
    </BounceText>
  );
};

// 4. Elastic Effect Component
const ElasticTextStyled = styled(Typography, {
  shouldForwardProp: (prop) => !['delay', 'trigger'].includes(prop as string),
})<{ delay: number; trigger: boolean }>(({ delay, trigger }) => ({
  opacity: trigger ? 1 : 0,
  animation: trigger ? `${elastic} 1.2s ease-out ${delay}s both` : "none",
  transformOrigin: "center",
}));

export const ElasticText: React.FC<BaseTextProps> = ({
  children,
  delay = 0,
  trigger = true,
  component = Typography,
  sx = {},
}) => {
  const Component = component;
  return (
    <ElasticTextStyled as={Component} delay={delay} trigger={trigger} sx={sx}>
      {children}
    </ElasticTextStyled>
  );
};

// 5. Wave Effect for Letters
interface WaveTextProps {
  text: string;
  waveDelay?: number;
  delay?: number;
  duration?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

const WaveChar = styled("span", {
  shouldForwardProp: (prop) => !['delay', 'trigger'].includes(prop as string),
})<{ delay: number; trigger: boolean }>(({ delay, trigger }) => ({
  display: "inline-block",
  animation: trigger ? `${wave} 2s ease-in-out ${delay}s infinite` : "none",
}));

export const WaveText: React.FC<WaveTextProps> = ({
  text,
  waveDelay = 0.1,
  delay = 0,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  return (
    <Component sx={sx}>
      {text.split("").map((char, index) => (
        <WaveChar key={index} delay={delay + index * waveDelay} trigger={trigger}>
          {char === " " ? "\u00A0" : char}
        </WaveChar>
      ))}
    </Component>
  );
};

// 6. Shimmer/Gradient Effect
const ShimmerTextStyled = styled(Typography, {
  shouldForwardProp: (prop) => !['trigger', 'delay'].includes(prop as string),
})<{ trigger: boolean; delay: number }>(({ trigger, delay }) => ({
  background: "linear-gradient(90deg, #3e4044, #29BC62, #F5933D, #3e4044)",
  backgroundSize: "200% auto",
  color: "transparent",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  animation: trigger ? `${shimmer} 20s ease-in-out ${delay}s infinite` : "none",
}));

export const ShimmerText: React.FC<BaseTextProps> = ({
  children,
  delay = 0,
  trigger = true,
  component = Typography,
  sx = {},
}) => {
  const Component = component;
  return (
    <ShimmerTextStyled as={Component} trigger={trigger} delay={delay} sx={sx}>
      {children}
    </ShimmerTextStyled>
  );
};

// 7. Glow Pulse Effect
const GlowText = styled(Typography, {
  shouldForwardProp: (prop) => !['trigger', 'delay'].includes(prop as string),
})<{ trigger: boolean; delay: number }>(({ trigger, delay }) => ({
  animation: trigger ? `${glowPulse} 2s ease-in-out ${delay}s infinite` : "none",
  color: "#29BC62",
}));

export const GlowPulseText: React.FC<BaseTextProps> = ({
  children,
  delay = 0,
  trigger = true,
  component = Typography,
  sx = {},
}) => {
  const Component = component;
  return (
    <GlowText as={Component} trigger={trigger} delay={delay} sx={sx}>
      {children}
    </GlowText>
  );
};

// 8. Flip In Effect
const FlipText = styled(Typography, {
  shouldForwardProp: (prop) => !['delay', 'trigger'].includes(prop as string),
})<{ delay: number; trigger: boolean }>(({ delay, trigger }) => ({
  opacity: trigger ? 1 : 0,
  animation: trigger ? `${flipIn} 0.8s ease-out ${delay}s both` : "none",
  transformOrigin: "center",
  perspective: "1000px",
}));

export const FlipInText: React.FC<BaseTextProps> = ({
  children,
  delay = 0,
  trigger = true,
  component = Typography,
  sx = {},
}) => {
  const Component = component;
  return (
    <FlipText as={Component} delay={delay} trigger={trigger} sx={sx}>
      {children}
    </FlipText>
  );
};

// 9. Reveal From Bottom (Curtain Effect)
interface RevealTextProps {
  text: string;
  revealHeight?: string;
  delay?: number;
  duration?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

const RevealContainer = styled(Box, {
  shouldForwardProp: (prop) => !['trigger', 'delay'].includes(prop as string),
})<{ trigger: boolean; delay: number }>(({ trigger, delay }) => ({
  overflow: "hidden",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#29BC62",
    transform: trigger ? "translateY(-100%)" : "translateY(0)",
    transition: `transform 1s cubic-bezier(0.25, 1, 0.5, 1) ${delay}s`,
  },
}));

export const RevealText: React.FC<RevealTextProps> = ({
  text,
  delay = 0,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  return (
    <RevealContainer trigger={trigger} delay={delay}>
      <Component sx={sx}>{text}</Component>
    </RevealContainer>
  );
};

// 10. Split Text with Custom Animations
interface SplitTextProps {
  text: string;
  splitBy?: "letters" | "words" | "lines";
  animationType?: "slideUp" | "slideDown" | "slideLeft" | "slideRight" | "fade" | "scale" | "rotate";
  staggerDelay?: number;
  delay?: number;
  duration?: number;
  trigger?: boolean;
  component?: React.ElementType;
  sx?: any;
}

const SplitChar = styled("span", {
  shouldForwardProp: (prop) => !['delay', 'trigger', 'animationType'].includes(prop as string),
})<{
  delay: number;
  trigger: boolean;
  animationType: string;
}>(({ delay, trigger, animationType }) => {
  const getAnimation = () => {
    switch (animationType) {
      case "slideUp":
        return trigger ? `${slideInUpStagger} 0.8s ease-out ${delay}s both` : "none";
      case "slideDown":
        return trigger ? `${slideInUpStagger} 0.8s ease-out ${delay}s both reverse` : "none";
      case "slideLeft":
        return trigger ? `${slideInUpStagger} 0.8s ease-out ${delay}s both` : "none";
      case "slideRight":
        return trigger ? `${slideInUpStagger} 0.8s ease-out ${delay}s both` : "none";
      case "fade":
        return trigger ? `${fadeInChar} 0.6s ease-out ${delay}s both` : "none";
      case "scale":
        return trigger ? `${bounceIn} 0.8s ease-out ${delay}s both` : "none";
      case "rotate":
        return trigger ? `${flipIn} 0.8s ease-out ${delay}s both` : "none";
      default:
        return trigger ? `${fadeInChar} 0.6s ease-out ${delay}s both` : "none";
    }
  };

  return {
    display: "inline-block",
    opacity: trigger ? 1 : 0,
    animation: getAnimation(),
  };
});

export const SplitText: React.FC<SplitTextProps> = ({
  text,
  splitBy = "letters",
  animationType = "slideUp",
  staggerDelay = 0.05,
  delay = 0,
  trigger = true,
  component: Component = Typography,
  sx = {},
}) => {
  const splitText = () => {
    if (splitBy === "words") {
      return text.split(" ").map((word, index) => ({ text: word, index }));
    } else if (splitBy === "lines") {
      return text.split("\n").map((line, index) => ({ text: line, index }));
    } else {
      return text.split("").map((char, index) => ({ text: char, index }));
    }
  };

  const textParts = splitText();

  return (
    <Component sx={sx}>
      {textParts.map(({ text: part, index }) => (
        <SplitChar key={index} delay={delay + index * staggerDelay} trigger={trigger} animationType={animationType}>
          {part === " " ? "\u00A0" : part}
          {splitBy === "words" && index < textParts.length - 1 ? " " : ""}
        </SplitChar>
      ))}
    </Component>
  );
};

// ================== UTILITY HOOK ==================

export const useTextAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold]);

  return { elementRef, isVisible };
};

// Export all components for easy importing
export default {
  TypewriterText,
  StaggeredText,
  BouncingText,
  ElasticText,
  WaveText,
  ShimmerText,
  GlowPulseText,
  FlipInText,
  RevealText,
  SplitText,
  useTextAnimation,
};
